import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import { getLibrary, getCustomers, getExportTemplates } from '../../api'
import { loadErrorMessage, settledList } from '../../utils/loadState'
import './ExportCenter.css'

const FORMAT_ICONS = { xlsx: '📗', docx: '📘', pptx: '📙', pdf: '📕' }

function guessFormat(exportResult) {
  if (!exportResult) return 'xlsx'
  const fn = exportResult.filename || exportResult.format || ''
  if (fn.includes('pdf')) return 'pdf'
  if (fn.includes('docx') || fn.includes('doc')) return 'docx'
  if (fn.includes('pptx') || fn.includes('ppt')) return 'pptx'
  return 'xlsx'
}

function formatDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const now = new Date()
  const diff = now - d
  if (diff < 3600000) return `${Math.round(diff / 60000)}m ago`
  if (diff < 86400000) return `${Math.round(diff / 3600000)}h ago`
  if (diff < 604800000) return `${Math.round(diff / 86400000)}d ago`
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function ExportCenter() {
  const { customerId } = useParams()
  const navigate = useNavigate()
  const [exports, setExports] = useState([])
  const [templates, setTemplates] = useState([])
  const [customers, setCustomers] = useState([])
  const [customerMap, setCustomerMap] = useState({})
  const [loading, setLoading] = useState(true)
  // Each list that failed to load, by name: shown as an error with a Retry, never as
  // "No exports yet" / "No templates uploaded yet" (item 201).
  const [errors, setErrors] = useState({})
  // Also completed work efforts for the "also available" section
  const [completedNoExport, setCompletedNoExport] = useState([])

  async function load() {
    setLoading(true)
    try {
      const [lib, cust, tmpl, done] = await Promise.allSettled([
        getLibrary({ state: 'export', page_size: 100 }),
        getCustomers(),
        getExportTemplates(),
        getLibrary({ state: 'complete', page_size: 50 }),
      ])
      const failed = {}

      // Exports = work efforts that reached "export" state (have export_result)
      const ex = settledList(lib, 'items')
      if (ex.error) failed.exports = ex.error
      setExports((ex.items || []).filter(i => i.has_export))

      const cu = settledList(cust, 'contexts', 'customers')
      if (cu.error) failed.customers = cu.error
      const list = cu.items || []
      setCustomers(list)
      const map = {}
      list.forEach(c => { map[c.customer_id] = c.company_name || c.customer_id })
      setCustomerMap(map)

      const tp = settledList(tmpl, 'templates')
      if (tp.error) failed.templates = tp.error
      setTemplates(tp.items || [])

      const dn = settledList(done, 'items')
      if (dn.error) failed.ready = dn.error
      setCompletedNoExport((dn.items || []).filter(i => !i.has_export))

      setErrors(failed)
    } catch (err) {
      setErrors({ exports: loadErrorMessage(err) })
    } finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  const breadcrumbs = customerId
    ? [{ label: 'Customers', href: '/customers' }, { label: customerId, href: `/customers/${customerId}` }, { label: 'Exports' }]
    : [{ label: 'Export Center' }]

  const navActions = (
    <>
      <Link to="/threads" className="nav-btn">Thread Library</Link>
      <Link to="/exports/templates" className="nav-btn">Templates</Link>
    </>
  )

  if (loading) return (
    <>
      <TopNav breadcrumbs={breadcrumbs} actions={navActions} />
      <div className="ec-loading"><div className="ec-spinner" /><p>Loading…</p></div>
    </>
  )

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} actions={navActions} />

      <div className="ec-page">
        <div className="ec-container">

          {/* Header */}
          <div className="ec-header">
            <div>
              <h1 className="ec-title">Export Center</h1>
              <p className="ec-subtitle">All generated deliverables across your customers</p>
            </div>
            <div className="ec-header-stats">
              <div className="ec-hstat">
                <div className="ec-hstat-value">{errors.exports ? '—' : exports.length}</div>
                <div className="ec-hstat-label">Exports Generated</div>
              </div>
              <div className="ec-hstat">
                <div className="ec-hstat-value">{errors.ready ? '—' : completedNoExport.length}</div>
                <div className="ec-hstat-label">Ready to Export</div>
              </div>
            </div>
          </div>

          <div className="ec-layout">
            {/* Main content */}
            <div className="ec-main">

              {/* Exported work efforts */}
              <div className="ec-section">
                <h2 className="ec-section-title">Generated Exports</h2>
                {errors.exports ? (
                  <LoadError what="exports" error={errors.exports} onRetry={load} />
                ) : exports.length === 0 ? (
                  <div className="ec-empty">
                    <div className="ec-empty-icon">📄</div>
                    <h3>No exports yet</h3>
                    <p>Complete a Work Effort and use the Export tab to generate a deliverable.</p>
                  </div>
                ) : (
                  <div className="ec-export-list">
                    {exports.map(item => {
                      const fmt = guessFormat(item)
                      return (
                        <div
                          key={item.id}
                          className="ec-export-row"
                          onClick={() => navigate(`/customers/${item.customer_id}/work-efforts/${item.id}`)}
                        >
                          <div className="ec-export-icon">{FORMAT_ICONS[fmt] || '📗'}</div>
                          <div className="ec-export-info">
                            <div className="ec-export-title">{item.title || 'Untitled Export'}</div>
                            <div className="ec-export-meta">
                              {customerMap[item.customer_id] || item.customer_id}
                              {item.findings_count > 0 && ` · ${item.findings_count} findings`}
                              {item.actions_count > 0 && ` · ${item.actions_count} actions`}
                            </div>
                          </div>
                          <div className="ec-export-findings">
                            {item.critical_count > 0 && (
                              <span className="ec-pill critical">{item.critical_count} critical</span>
                            )}
                            {item.warning_count > 0 && (
                              <span className="ec-pill warning">{item.warning_count}</span>
                            )}
                          </div>
                          <div className="ec-export-date">{formatDate(item.updated_at)}</div>
                          <div className="ec-export-action">View →</div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Completed but not yet exported */}
              {completedNoExport.length > 0 && (
                <div className="ec-section">
                  <h2 className="ec-section-title">Ready to Export</h2>
                  <p className="ec-section-desc">These investigations are complete but haven't generated an export yet.</p>
                  <div className="ec-export-list">
                    {completedNoExport.slice(0, 10).map(item => (
                      <div
                        key={item.id}
                        className="ec-export-row ec-export-row-pending"
                        onClick={() => navigate(`/customers/${item.customer_id}/work-efforts/${item.id}`)}
                      >
                        <div className="ec-export-icon" style={{ opacity: 0.4 }}>📗</div>
                        <div className="ec-export-info">
                          <div className="ec-export-title">{item.title || 'Untitled'}</div>
                          <div className="ec-export-meta">
                            {customerMap[item.customer_id] || item.customer_id}
                            {item.findings_count > 0 && ` · ${item.findings_count} findings`}
                          </div>
                        </div>
                        <div className="ec-export-findings">
                          {item.critical_count > 0 && (
                            <span className="ec-pill critical">{item.critical_count}</span>
                          )}
                        </div>
                        <div className="ec-export-date">{formatDate(item.updated_at)}</div>
                        <div className="ec-export-action" style={{ color: 'var(--green)' }}>Export →</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right sidebar — Templates */}
            <div className="ec-sidebar">
              <div className="ec-sb-header">
                <h3 className="ec-sb-title">Templates</h3>
                <Link to="/exports/templates" className="ec-sb-link">Manage →</Link>
              </div>
              <p className="ec-sb-desc">Available templates for generating deliverables</p>

              {errors.templates ? (
                <LoadError inline what="templates" error={errors.templates} onRetry={load} />
              ) : templates.length > 0 ? (
                <div className="ec-sb-list">
                  {templates.map((t, i) => (
                    <Link
                      key={i}
                      to={`/exports/templates/${t.template_id || t.name}`}
                      className="ec-sb-template"
                    >
                      <div className="ec-sb-icon">{FORMAT_ICONS[t.format] || '📘'}</div>
                      <div className="ec-sb-info">
                        <div className="ec-sb-name">{t.name || t.template_id}</div>
                        <div className="ec-sb-meta">
                          {t.description ? t.description.substring(0, 50) : 'No description'}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="ec-sb-empty"><p>No templates uploaded yet</p></div>
              )}

              {/* Customer quick links */}
              {errors.customers && (
                <LoadError inline what="customers" error={errors.customers} onRetry={load} />
              )}
              {customers.length > 0 && (
                <>
                  <h3 className="ec-sb-title" style={{ marginTop: 20 }}>By Customer</h3>
                  <div className="ec-sb-list">
                    {customers.slice(0, 8).map(c => (
                      <Link
                        key={c.customer_id}
                        to={`/customers/${c.customer_id}/exports`}
                        className="ec-sb-template"
                      >
                        <div className="ec-sb-cust-avatar">
                          {(c.company_name || c.customer_id || '??').substring(0, 2).toUpperCase()}
                        </div>
                        <div className="ec-sb-info">
                          <div className="ec-sb-name">{c.company_name || c.customer_id}</div>
                          <div className="ec-sb-meta">{c.industry || '—'}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
